import {Box, Stack, Typography, useTheme} from '@mui/material';
import {AlertInfo} from 'presentation/components';
import Icon from 'presentation/icons';
import {translations} from 'presentation/translations-new';
import React, {Fragment} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {
	getInstructionsItems,
	INSTRUCTIONS_DISCLAIMER,
} from '../../pages/NewRequestInstructions/constants/instructions';
import {
	StyledBox,
	StyledDescription,
	StyledHeader,
	StyledItemDescription,
	StyledLink,
	StyledList,
	StyledListItem,
	StyledTitle,
} from './styles';
import {CardInstructionsTypes, CardInstructionsVariantEnum} from './types';
import {useHostName} from 'presentation/hooks';
import {AuthorizationsEnum} from 'presentation/constants';
import {usePpoNetworkStore} from 'presentation/modules/guide/shared/store/guideStore/usePpoNetworkStore';
import {ppoNetworkTypeEnum} from 'presentation/modules/guide/shared/store/guideStore/types';

const {newRequestInstructions} = translations['pt-br'].privateFlow.requestAuthorizations;

export const CardInstructions = ({
	variant = CardInstructionsVariantEnum.MEDIUM,
	title,
	hideDescription = false,
}: CardInstructionsTypes) => {
	return (
		<StyledBox $variant={variant}>
			<StyledHeader $variant={variant}>
				<StyledTitle $variant={variant}>
					{title ?? newRequestInstructions.subtitle}
				</StyledTitle>
				{hideDescription ? null : (
					<StyledDescription $variant={variant}>
						<b>{newRequestInstructions.description.line1}</b>
						{newRequestInstructions.description.line2}
					</StyledDescription>
				)}
			</StyledHeader>
			<CardInstructionsContent variant={variant} />
		</StyledBox>
	);
};

export const CardInstructionsContent = ({
	variant = CardInstructionsVariantEnum.MEDIUM,
}: Pick<CardInstructionsTypes, 'variant'>) => {
	const navigate = useNavigate();
	const {pathname} = useLocation();
	const {colors} = useTheme();
	const {isNotredameSaude} = useHostName();
	const setPpoNetworkType = usePpoNetworkStore(state => state.setPpoNetworkType);
	const instructionsItems = getInstructionsItems(isNotredameSaude);

	const isSmall = variant === CardInstructionsVariantEnum.SMALL;

	return (
		<Fragment>
			{instructionsItems.map((item, index) => {
				const isUrgencyAndEmergencyItem = index === 0;
				const shouldHideNetworkLink =
					isNotredameSaude &&
					pathname === AuthorizationsEnum.INSTRUCTIONS &&
					isUrgencyAndEmergencyItem;

				return (
					<Stack key={index} direction="row" alignItems="center" gap={2} pr={1}>
						<Icon
							style={{
								minWidth: 24,
								alignSelf: isUrgencyAndEmergencyItem && !isSmall ? 'center' : 'baseline',
							}}
							name={item.iconName}
							color={colors.light.text.secondary}
						/>
						<Stack>
							<StyledItemDescription $variant={variant}>
								{item.text}
								{item.strongText ? <strong> {item.strongText}</strong> : ''}
								{item.continuationText}
							</StyledItemDescription>
							<Box>
								{item.action && !shouldHideNetworkLink ? (
									<StyledLink
										data-tag-id={item.action?.dataTagId}
										$variant={variant}
										onClick={() => {
											if (isNotredameSaude && isUrgencyAndEmergencyItem)
												setPpoNetworkType(
													ppoNetworkTypeEnum.ACCREDITED_NETWORK,
												);

											if (item.action?.navigateTo) {
												navigate(item.action.navigateTo, {
													state: pathname,
												});
												return;
											}

											window.open(item.action?.externalLink);
										}}>
										{item.action?.name}
									</StyledLink>
								) : null}

								{item.bullets ? (
									<StyledList>
										{item.bullets.map((bullet, bulletIndex) => (
											<StyledListItem key={bulletIndex} $variant={variant}>
												{bullet}
											</StyledListItem>
										))}
									</StyledList>
								) : null}

							</Box>
						</Stack>
					</Stack>
				);
			})}
			<AlertInfo.Root severity="info" icon={<Icon name="info-outlined" size={22} />}>
				<AlertInfo.Description>
					<Typography variant="body2">{INSTRUCTIONS_DISCLAIMER}</Typography>
				</AlertInfo.Description>
			</AlertInfo.Root>
		</Fragment>
	);
};
